/**
 * The dictionary. Arabic is the source language — this product ships to Arabic-speaking
 * facilities operators first, and English is the translation, not the other way round.
 *
 * `en` is typed FROM `ar`, so a key added to Arabic and forgotten in English is a compile
 * error, not a silently-missing string at 7am in front of a customer.
 */

export type Lang = "ar" | "en";

export const LANGS: readonly Lang[] = ["ar", "en"] as const;

export function dirOf(lang: Lang): "rtl" | "ltr" {
  return lang === "ar" ? "rtl" : "ltr";
}

const ar = {
  // ---- product / chrome
  "app.name": "تانكلي",
  "app.tagline": "نظام تشغيل وقود الطاقة الاحتياطية",
  "nav.dashboard": "لوحة التشغيل",
  "nav.sites": "المواقع",
  "nav.monitoring": "المراقبة",
  "nav.devices": "الأجهزة",
  "nav.refills": "التعبئات",
  "nav.requests": "طلبات التعبئة",
  "nav.reports": "التقارير",
  "nav.settings": "الإعدادات",
  "nav.signOut": "تسجيل الخروج",
  "nav.skipToContent": "تخطَّ إلى المحتوى",
  "lang.toggle": "English",
  // The 2-letter code shown next to the globe icon: the language you switch TO.
  "lang.toggleShort": "EN",
  "lang.switchTo": "التبديل إلى الإنجليزية",

  // ---- generic
  "common.save": "حفظ",
  "common.saving": "جارٍ الحفظ…",
  "common.cancel": "إلغاء",
  "common.create": "إنشاء",
  "common.delete": "حذف",
  "common.edit": "تعديل",
  "common.close": "إغلاق",
  "common.back": "رجوع",
  "common.next": "التالي",
  "common.search": "بحث",
  "common.filter": "تصفية",
  "common.clear": "مسح",
  "common.retry": "إعادة المحاولة",
  "common.loading": "جارٍ التحميل…",
  "common.optional": "اختياري",
  "common.required": "مطلوب",
  "common.none": "—",
  "common.all": "الكل",
  "common.liters": "لتر",
  "common.hours": "ساعة",
  "common.copy": "نسخ",
  "common.copied": "تم النسخ",
  "common.loadMore": "تحميل المزيد",
  "common.of": "من",
  "common.actions": "إجراءات",
  "common.continue": "متابعة",
  "common.skip": "تخطّي",
  "common.done": "إنهاء",

  // ---- errors / states
  "error.title": "تعذّر تحميل البيانات",
  "error.generic": "حدث خطأ غير متوقّع. حاول مرة أخرى.",
  "error.network": "تعذّر الوصول إلى الخادم. تحقّق من اتصالك.",
  "error.notFound": "غير موجود",
  "error.notFoundBody": "الصفحة التي تبحث عنها غير موجودة.",
  "error.forbidden": "لا تملك صلاحية لهذا الإجراء",
  "error.unauthorized": "انتهت جلستك. سجّل الدخول مرة أخرى.",
  "empty.title": "لا توجد بيانات بعد",

  // ---- auth
  //
  // The tank panel on the sign-in screen. This is the product's opening sentence, so it
  // says the one thing the product is for — not a slogan about "empowering operations".
  // The reading it shows is real and it is bad: a branch four hours from darkness.
  "auth.tankSite": "الموقع",
  "auth.tankSiteName": "فرع الكرادة",
  // ROSE MEANS ONE THING: about to RUN OUT. It is a statement about TIME, not about a
  // percentage — src/domain/risk.ts calls a tank `dry` when it has days left, not when it
  // is below a line. This panel used to say "low fuel" in the dry colour, which taught the
  // exact wrong association on the very first screen anyone sees.
  "auth.tankLow": "ينفد خلال 4 أيام",
  "auth.tankLine": "تعرف كم لديك من وقود، وأين ذهب، وأيّ موقع يوشك على النفاد — قبل أن ينفد.",
  "auth.legalLine": "تانكلي — نظام تشغيل وقود الطاقة الاحتياطية",

  "auth.signIn": "تسجيل الدخول",
  "auth.signInSubtitle": "ادخل إلى مساحة عمل مؤسستك.",
  "auth.signUp": "إنشاء حساب",
  "auth.signUpSubtitle": "ابدأ بتتبّع كل لتر.",
  "auth.email": "البريد الإلكتروني",
  "auth.password": "كلمة المرور",
  "auth.name": "الاسم الكامل",
  "auth.google": "المتابعة عبر Google",
  "auth.or": "أو",
  "auth.forgot": "نسيت كلمة المرور؟",
  "auth.noAccount": "ليس لديك حساب؟",
  "auth.hasAccount": "لديك حساب بالفعل؟",
  "auth.passwordHint": "١٢ حرفًا على الأقل.",
  "auth.forgotTitle": "إعادة تعيين كلمة المرور",
  "auth.forgotSubtitle": "سنرسل رابط إعادة التعيين إلى بريدك إن كان مسجّلًا لدينا.",
  "auth.forgotSend": "إرسال الرابط",
  "auth.forgotSent": "إن كان هذا البريد مسجّلًا، فقد أُرسل إليه رابط إعادة التعيين. تحقّق من بريدك.",
  "auth.resetTitle": "اختر كلمة مرور جديدة",
  "auth.resetSubtitle": "سيُبطل هذا الرابط بعد الاستخدام.",
  "auth.resetSubmit": "تعيين كلمة المرور",
  "auth.resetDone": "تم تحديث كلمة المرور. يمكنك تسجيل الدخول الآن.",
  "auth.verifyTitle": "تأكيد البريد الإلكتروني",
  "auth.verifying": "جارٍ التأكيد…",
  "auth.verifyDone": "تم تأكيد بريدك الإلكتروني.",
  "auth.verifyFailed": "رابط التأكيد غير صالح أو منتهي الصلاحية.",
  "auth.verifyResend": "إعادة إرسال رسالة التأكيد",
  "auth.verifySent": "أُرسلت رسالة تأكيد جديدة.",
  "auth.checkEmail": "تحقّق من بريدك",
  "auth.checkEmailBody": "أرسلنا رابط تأكيد إلى {email}. أكّد بريدك ثم سجّل الدخول.",
  "auth.emailUnverified": "لم يتم تأكيد بريدك الإلكتروني بعد.",

  // ---- invitation
  "invite.title": "دعوة للانضمام",
  "invite.body": "دعاك {inviter} للانضمام إلى {org} بصفة {role}.",
  "invite.bodyNoInviter": "لقد تمت دعوتك للانضمام إلى {org} بصفة {role}.",
  "invite.accept": "قبول الدعوة",
  "invite.accepting": "جارٍ القبول…",
  "invite.forEmail": "هذه الدعوة موجّهة إلى {email}. سجّل الدخول بهذا البريد لقبولها.",
  "invite.expires": "تنتهي صلاحية الدعوة في {date}.",
  "invite.invalid": "هذه الدعوة غير صالحة أو انتهت صلاحيتها.",
  "invite.signInFirst": "سجّل الدخول لقبول الدعوة",

  // ---- onboarding
  "onboarding.step": "الخطوة {n} من {total}",
  "onboarding.createOrg.title": "أنشئ مساحة عمل مؤسستك",
  "onboarding.createOrg.subtitle": "كل خزّان وكل مولّد وكل لتر — داخل هذه المساحة، ولا يراها أحد غيرك.",
  "onboarding.createOrg.name": "اسم المؤسسة",
  "onboarding.createOrg.nameHint": "بأي لغة. سنتكفّل بالباقي.",
  "onboarding.createOrg.namePlaceholder": "اسم مؤسستك",
  "onboarding.createOrg.slug": "معرّف المساحة",
  "onboarding.createOrg.slugHint": "هذا هو رابط مساحة عملك.",
  "onboarding.createOrg.slugChecking": "جارٍ التحقّق…",
  "onboarding.createOrg.slugAvailable": "متاح",
  "onboarding.createOrg.slugTaken": "هذا المعرّف مستخدم بالفعل.",
  "onboarding.createOrg.slugInvalid": "حروف لاتينية صغيرة وأرقام وشرطات فقط.",
  "onboarding.createOrg.submit": "إنشاء المؤسسة",
  "onboarding.invite.title": "ادعُ فريقك",
  "onboarding.invite.subtitle": "الوقود عمل جماعي. أضف من يسجّل التعبئات ومن يوافق عليها.",
  "onboarding.invite.add": "إضافة دعوة أخرى",
  "onboarding.invite.send": "إرسال الدعوات",
  "onboarding.invite.sent": "أُرسلت {n} دعوة.",
  "onboarding.invite.later": "سأفعل ذلك لاحقًا",
  "onboarding.site.title": "أضف أول موقع",
  "onboarding.site.subtitle": "الموقع هو الفرع أو المستشفى أو البرج — وهو ما تنتمي إليه الخزّانات والمولّدات.",
  "onboarding.site.submit": "إنشاء الموقع",
  "onboarding.welcome": "مساحة عملك جاهزة.",
  "onboarding.rail.org": "المؤسسة",
  "onboarding.rail.team": "الفريق",
  "onboarding.rail.site": "الموقع",
  "onboarding.site.created": "أُنشئ الموقع «{name}».",
  "onboarding.tank.title": "والآن أول خزّان فيه",
  "onboarding.tank.subtitle": "الخزّان هو ما نراقبه. سعته وحدّ انخفاضه هما ما يجعل تانكلي ينبّهك قبل أن ينفد الوقود، لا بعده.",
  "onboarding.tank.capacityHint": "كم يسع الخزّان وهو ممتلئ، باللترات.",
  "onboarding.tank.initialHint": "كم فيه الآن؟ اتركه فارغًا إن لم تكن تعرف.",
  "onboarding.tank.submit": "إنهاء الإعداد",
  "onboarding.tank.finishing": "جارٍ تجهيز مساحتك…",

  // ---- org switcher
  "org.switcher.label": "المؤسسة الحالية",
  "org.switcher.switch": "تبديل المؤسسة",
  "org.switcher.create": "إنشاء مؤسسة جديدة",
  "org.switcher.switching": "جارٍ التبديل…",
  "org.suspended": "هذه المؤسسة موقوفة. الوصول للقراءة فقط.",
  "org.impersonating": "أنت تعرض هذه المؤسسة بصفة موظّف دعم. الجلسة للقراءة فقط.",

  // ---- roles
  "role.owner": "مالك",
  "role.admin": "مسؤول",
  "role.operator": "مشغّل",
  "role.viewer": "مطّلع",
  "role.owner.hint": "كل الصلاحيات، بما فيها مفاتيح الواجهة البرمجية.",
  "role.admin.hint": "إدارة المواقع والتعبئات والأعضاء.",
  "role.operator.hint": "تسجيل التعبئات ورفع الطلبات.",
  "role.viewer.hint": "الاطّلاع فقط.",

  // ---- dashboard
  "dash.title": "لوحة التشغيل",
  "dash.greeting": "صباح الخير، {name}",
  "dash.attention": "يحتاج انتباهك",
  "dash.attentionEmpty": "لا شيء على وشك الخطأ.",
  "dash.attentionEmptyBody": "كل الخزّانات فوق الحدّ الأدنى ولا توجد تعبئات متأخّرة.",
  "dash.dry": "على وشك النفاد",
  "dash.low": "تحت الحدّ الأدنى",
  "dash.overdue": "تعبئة متأخّرة",
  "dash.pendingRequests": "طلبات بانتظار القرار",
  // `{days}` is a phrase from Intl.RelativeTimeFormat ("خلال ٤ أيام"), NOT a bare number —
  // it is the only thing that gets Arabic day-agreement right (يوم / يومان / أيام / يومًا)
  // across every count, in both languages, without us hand-rolling a plural table.
  "dash.runsDry": "ينفد {days}",
  "dash.runsDryUnknown": "معدّل الاستهلاك غير معروف بعد",
  "dash.lastRefill": "آخر تعبئة: {when}",
  "dash.neverRefilled": "لم تُسجّل أي تعبئة",
  "dash.overdueBy": "آخر تعبئة {days}",
  "dash.healthy": "سليم",
  "dash.remaining": "المتبقّي",
  "dash.runway": "الوقت المتبقّي",
  "dash.recent": "آخر النشاط",
  "dash.viewAll": "عرض الكل",
  "dash.thisMonth": "هذا الشهر",
  "dash.litersAdded": "لترات مضافة",
  "dash.refillsRecorded": "تعبئات مسجّلة",
  "dash.litersPerHour": "لتر/ساعة",
  "dash.sitesReporting": "مواقع مُبلِّغة",
  "dash.fleet": "حالة الخزّانات",
  "dash.viewSite": "عرض الموقع",
  "dash.recordRefill": "تسجيل تعبئة",
  "dash.reviewRequest": "مراجعة الطلب",

  // ---- sites
  "sites.title": "المواقع",
  "sites.new": "موقع جديد",
  "sites.empty": "لم تُضِف أي موقع بعد.",
  "sites.emptyBody": "الموقع هو الفرع أو المستشفى أو البرج. أضف أوّل موقع لتبدأ تسجيل التعبئات.",
  "sites.name": "الاسم",
  "sites.code": "الرمز",
  "sites.region": "المنطقة",
  "sites.address": "العنوان",
  "sites.status": "الحالة",
  "sites.generators": "المولّدات",
  "sites.tanks": "الخزّانات",
  "sites.lastRefill": "آخر تعبئة",
  "sites.searchPlaceholder": "ابحث بالاسم أو الرمز…",
  "sites.status.active": "نشط",
  "sites.status.inactive": "متوقّف",
  "sites.detail.tanks": "الخزّانات",
  "sites.detail.generators": "المولّدات",
  "sites.detail.history": "سجلّ التعبئات",
  "sites.detail.noTanks": "لا توجد خزّانات في هذا الموقع.",
  "sites.detail.noGenerators": "لا توجد مولّدات في هذا الموقع.",
  "sites.detail.noRefills": "لم تُسجّل أي تعبئة في هذا الموقع.",
  "sites.addTank": "إضافة خزّان",
  "sites.addGenerator": "إضافة مولّد",
  "sites.deleteConfirm": "حذف {name}؟ سيُحذف معه كل مولّداته وخزّاناته.",

  // The sites list leads with the LEVEL, not the name: a موقع is not a صف بيانات، بل مكان
  // فيه وقود أو يوشك ألّا يكون.
  "sites.worstTank": "أدنى خزّان",
  "sites.noTanksYet": "لا خزّانات",
  "sites.allRegions": "كل المناطق",
  "sites.filterRegion": "تصفية بالمنطقة",
  "sites.edit": "تعديل الموقع",
  "sites.editTitle": "تعديل الموقع",
  "sites.noRegion": "بدون منطقة",

  // ARCHIVE, NOT DELETE. The word on the button is the word for what happens.
  "sites.archive": "أرشفة الموقع",
  "sites.archiveTitle": "أرشفة {name}؟",
  "sites.archiveBody":
    "يخرج الموقع من القوائم ولا يقبل تعبئات جديدة، ويتوقّف عن رفع التنبيهات. يبقى سجلّ وقوده كاملًا في السجلّ والتقارير، ويمكنك إعادته إلى الخدمة متى شئت. لا يُحذف شيء.",
  "sites.archived": "مؤرشف",
  "sites.archivedNotice": "هذا الموقع مؤرشف: لا يقبل تعبئات جديدة، وسجلّه محفوظ كما هو.",
  "sites.restore": "إعادة إلى الخدمة",
  "sites.showArchived": "إظهار المؤرشف",

  // ---- monitoring
  "monitoring.title": "المراقبة",
  "monitoring.subtitle": "قراءات الخزّانات والمولّدات لحظة بلحظة، مباشرة من الأجهزة في الموقع.",
  "monitoring.live": "مباشر",
  // `{when}` is a phrase from Intl.RelativeTimeFormat ("قبل ٣ دقائق") — see monitoring.stale.
  "monitoring.updated": "تحدّث {when}",
  "monitoring.neverReported": "لم يُبلِّغ بعد",
  "monitoring.tanksSection": "مستوى الخزّانات المباشر",
  "monitoring.generatorsSection": "حالة المولّدات المباشرة",
  "monitoring.tanksEmpty": "لا توجد خزّانات نشطة بعد.",
  "monitoring.generatorsEmpty": "لا توجد مولّدات نشطة بعد.",
  "monitoring.tankTrendTitle": "اتجاه مستوى الوقود",
  "monitoring.generatorTrendTitle": "اتجاه ساعات التشغيل",
  "monitoring.last24h": "آخر ٢٤ ساعة",
  "monitoring.selectTank": "اختر خزّانًا",
  "monitoring.selectGenerator": "اختر مولّدًا",
  "monitoring.noTankSelected": "لا توجد خزّانات لعرض اتجاهها.",
  "monitoring.noGeneratorSelected": "لا توجد مولّدات لعرض اتجاهها.",
  "monitoring.noReadingsTitle": "لا قراءات مباشرة بعد",
  "monitoring.noReadingsTankBody": "وصّل جهاز استشعار بهذا الخزّان ليبدأ البث المباشر هنا.",
  "monitoring.noReadingsGeneratorBody": "وصّل جهاز استشعار بهذا المولّد ليبدأ البث المباشر هنا.",
  "monitoring.temperature": "الحرارة",
  "monitoring.chartSummary": "من {from} إلى {to}",

  // ---- tanks / generators
  "tank.name": "اسم الخزّان",
  "tank.capacity": "السعة",
  "tank.level": "المستوى",
  "tank.fuelType": "نوع الوقود",
  "tank.threshold": "حدّ الانخفاض",
  "tank.thresholdHint": "نسبة من السعة يُعتبر الخزّان تحتها منخفضًا. اتركه فارغًا لاستخدام إعداد المؤسسة.",
  "tank.initialLevel": "المستوى الابتدائي",
  "tank.levelUnknown": "المستوى غير معروف",
  "tank.fuel.diesel": "ديزل",
  "tank.fuel.petrol": "بنزين",
  "tank.fuel.kerosene": "كيروسين",
  "tank.status.active": "نشط",
  "tank.status.decommissioned": "خارج الخدمة",
  "gen.name": "اسم المولّد",
  "gen.kva": "القدرة (kVA)",
  "gen.runningHours": "ساعات التشغيل",
  "gen.manufacturer": "الصانع",
  "gen.model": "الطراز",
  "gen.serial": "الرقم التسلسلي",
  "gen.status.active": "نشط",
  "gen.status.maintenance": "صيانة",
  "gen.status.decommissioned": "خارج الخدمة",

  // A tank installed low is a READING, not a fault — and it will legitimately raise a
  // low-fuel alert the moment it is saved. Say so before the operator is surprised by it.
  "tank.initialLevelHint":
    "قراءة عدّاد، وليست تعبئة. خزّان يُسجَّل منخفضًا سيرفع تنبيه وقود منخفض فورًا — وهذا صحيح.",
  "tank.capacityBelowLevel": "أقلّ من آخر مستوى مسجّل ({level} لتر). لا يمكن أن تصغر السعة عن الوقود الذي فيها.",
  "tank.edit": "تعديل الخزّان",
  "tank.editTitle": "تعديل {name}",
  "tank.statusLabel": "الحالة",

  // DECOMMISSION, NOT DELETE. الحذف يخسر التاريخ؛ الإخراج من الخدمة يحفظه.
  "tank.decommission": "إخراج من الخدمة",
  "tank.decommissionTitle": "إخراج {name} من الخدمة؟",
  "tank.decommissionBody":
    "يخرج الخزّان من القوائم ولا يقبل تعبئات أو قراءات جديدة. تبقى كل لتراته المسجّلة في السجلّ والتقارير. لا يُحذف شيء، ويمكنك إعادته إلى الخدمة لاحقًا.",
  "tank.recommission": "إعادة إلى الخدمة",
  "tank.outOfService": "خارج الخدمة ({n})",
  "tank.hideOutOfService": "إخفاء ما هو خارج الخدمة",

  "gen.edit": "تعديل المولّد",
  "gen.editTitle": "تعديل {name}",
  "gen.statusLabel": "الحالة",
  "gen.runningHoursHint": "لا يرجع العدّاد للخلف أبدًا. آخر قراءة {current} ساعة.",
  "gen.decommission": "إخراج من الخدمة",
  "gen.decommissionTitle": "إخراج {name} من الخدمة؟",
  "gen.decommissionBody":
    "يخرج المولّد من القوائم ولا يمكن اختياره في تعبئة جديدة. تبقى ساعات تشغيله وكل تعبئاته في السجلّ والتقارير. لا يُحذف شيء، ويمكنك إعادته إلى الخدمة لاحقًا.",
  "gen.recommission": "إعادة إلى الخدمة",
  "gen.outOfService": "خارج الخدمة ({n})",
  "gen.hideOutOfService": "إخفاء ما هو خارج الخدمة",

  // ---- refills
  "refills.title": "التعبئات",
  "refills.new": "تسجيل تعبئة",
  "refills.empty": "لم تُسجّل أي تعبئة بعد.",
  "refills.emptyBody": "كل لتر يدخل خزّانًا يُسجَّل هنا. هذا هو دفتر الوقود.",
  "refills.site": "الموقع",
  "refills.tank": "الخزّان",
  "refills.generator": "المولّد",
  "refills.before": "قبل",
  "refills.added": "المضاف",
  "refills.after": "بعد",
  "refills.workingHours": "ساعات التشغيل",
  "refills.status": "الحالة",
  "refills.recordedAt": "وقت التعبئة",
  "refills.recordedBy": "سجّلها",
  "refills.notes": "ملاحظات",
  "refills.notesPlaceholder": "المورّد، رقم الشاحنة، أي فرق في الكمية…",
  "refills.status.pending": "قيد الانتظار",
  "refills.status.completed": "مكتملة",
  "refills.status.cancelled": "ملغاة",
  "refills.arithmetic": "الحساب",
  "refills.checkOk": "الحساب صحيح.",
  "refills.checkMismatch": "لا يتطابق: {before} + {added} = {expected}، وليس {after}.",
  "refills.checkOverCapacity": "أكبر من سعة الخزّان ({capacity} لتر).",
  "refills.checkHoursBackwards": "عدّاد الساعات لا يرجع للخلف. آخر قراءة {current}.",
  "refills.autoAfter": "احتُسب تلقائيًا",
  "refills.selectTank": "اختر خزّانًا",
  "refills.selectSite": "اختر موقعًا",
  "refills.selectGenerator": "بدون مولّد",
  "refills.tankLevelNow": "المستوى الحالي المسجّل: {liters} لتر",
  "refills.saved": "سُجّلت التعبئة.",
  "refills.deleteConfirm": "حذف هذه التعبئة؟",
  "refills.fromRequest": "تنفيذ الطلب",
  "refills.dateFrom": "من تاريخ",
  "refills.dateTo": "إلى تاريخ",
  "refills.amend": "تعديل",
  "refills.amendTitle": "تعديل التعبئة",
  "refills.amendHint": "التعبئة قيد الانتظار وحدها تُعدَّل أرقامها. المكتملة سجلّ مدقَّق.",
  "refills.amendStatus": "الحالة بعد التعديل",
  "refills.amended": "عُدِّلت التعبئة.",
  "refills.deleteBody":
    "تُحذف التعبئة نهائيًا. التعبئة المكتملة لا تُحذف — تُلغى، ليبقى دفتر الوقود قابلاً للتدقيق.",
  "refills.closedRecord": "التعبئة المكتملة سجلّ مدقَّق ولا تُعدَّل.",
  "refills.emptyFiltered": "لا تعبئات تطابق هذه التصفية.",

  // ---- refill requests
  "requests.title": "طلبات التعبئة",
  "requests.new": "طلب وقود",
  "requests.empty": "لا توجد طلبات.",
  "requests.emptyBody": "يرفع المشغّلون الطلبات عند الحاجة إلى الوقود، ويوافق عليها المسؤولون.",
  "requests.requested": "المطلوب",
  "requests.approved": "المعتمد",
  "requests.neededBy": "مطلوب قبل",
  "requests.requestedBy": "قدّمه",
  "requests.decidedBy": "قرّره",
  "requests.notes": "المبرّر",
  "requests.notesPlaceholder": "الخزّان عند ١٨٪، ويُتوقّع انقطاعان هذا الأسبوع.",
  "requests.decisionNote": "ملاحظة القرار",
  "requests.approve": "اعتماد",
  "requests.decline": "رفض",
  "requests.cancel": "سحب الطلب",
  "requests.approveTitle": "اعتماد الطلب",
  "requests.approveLiters": "اللترات المعتمدة",
  "requests.approveHint": "يمكنك اعتماد كمية أقلّ من المطلوبة.",
  "requests.declineTitle": "رفض الطلب",
  "requests.declineReason": "سبب الرفض",
  "requests.declineReasonHint": "يظهر للمشغّل. مطلوب — لا تتركه يخمّن.",
  "requests.fulfil": "تسجيل التعبئة",
  "requests.status.pending": "بانتظار القرار",
  "requests.status.approved": "معتمد",
  "requests.status.declined": "مرفوض",
  "requests.status.fulfilled": "منفَّذ",
  "requests.status.cancelled": "ملغى",

  // ---- reports
  "reports.title": "التقارير",
  "reports.from": "من",
  "reports.to": "إلى",
  "reports.run": "عرض التقرير",
  "reports.export": "تصدير",
  "reports.exportTitle": "تصدير تقرير",
  "reports.type": "نوع التقرير",
  "reports.format": "الصيغة",
  "reports.type.refills": "سجلّ التعبئات",
  "reports.type.consumption": "الاستهلاك والكفاءة",
  "reports.type.sites": "جرد المواقع",
  "reports.exports": "ملفات التصدير",
  "reports.exportsEmpty": "لم تُصدَّر أي ملفات بعد.",
  "reports.download": "تنزيل",
  "reports.export.pending": "قيد التحضير",
  "reports.export.ready": "جاهز",
  "reports.export.failed": "فشل",
  "reports.export.expired": "منتهي",
  "reports.requested": "طُلب التصدير. سيظهر هنا عند اكتماله.",
  "reports.noData": "لا توجد بيانات في هذه الفترة.",
  "reports.perSite": "التفصيل حسب الموقع",
  "reports.range.30": "آخر ٣٠ يومًا",
  "reports.range.90": "آخر ٩٠ يومًا",
  "reports.range.month": "هذا الشهر",
  "reports.range.year": "هذه السنة",

  // ---- settings
  "settings.title": "الإعدادات",
  "settings.profile": "ملف المؤسسة",
  "settings.members": "الأعضاء",
  "settings.invitations": "الدعوات",
  "settings.apiKeys": "مفاتيح الواجهة البرمجية",
  "settings.org.name": "اسم المؤسسة",
  "settings.org.slug": "المعرّف",
  "settings.org.slugImmutable": "لا يمكن تغيير المعرّف بعد الإنشاء.",
  "settings.org.timezone": "المنطقة الزمنية",
  "settings.org.threshold": "حدّ انخفاض الوقود",
  "settings.org.thresholdHint": "تُعتبر الخزّانات منخفضة عند هذه النسبة أو أقلّ.",
  "settings.org.overdue": "أيام تأخّر التعبئة",
  "settings.org.overdueHint": "يُعتبر الموقع متأخّرًا بعد هذا العدد من الأيام دون تعبئة.",
  "settings.org.contact": "بريد التنبيهات",
  "settings.saved": "حُفظت التغييرات.",
  "settings.members.invite": "دعوة عضو",
  "settings.members.role": "الدور",
  "settings.members.joined": "انضمّ في",
  "settings.members.lastActive": "آخر نشاط",
  "settings.members.remove": "إزالة",
  "settings.members.removeConfirm": "إزالة {name} من المؤسسة؟",
  "settings.members.leave": "مغادرة المؤسسة",
  "settings.members.you": "أنت",
  "settings.members.lastOwner": "لا يمكن إزالة المالك الأخير.",
  "settings.invite.email": "البريد الإلكتروني",
  "settings.invite.role": "الدور",
  "settings.invite.send": "إرسال الدعوة",
  "settings.invite.pending": "الدعوات المعلّقة",
  "settings.invite.none": "لا توجد دعوات معلّقة.",
  "settings.invite.revoke": "إبطال",
  "settings.invite.expires": "تنتهي في",
  "settings.invite.invitedBy": "دعاه",
  "settings.invite.sent": "أُرسلت الدعوة إلى {email}.",
  "settings.keys.new": "مفتاح جديد",
  "settings.keys.name": "الاسم",
  "settings.keys.prefix": "البادئة",
  "settings.keys.scopes": "الصلاحيات",
  "settings.keys.lastUsed": "آخر استخدام",
  "settings.keys.expires": "تنتهي",
  "settings.keys.never": "لم يُستخدم",
  "settings.keys.revoke": "إبطال",
  "settings.keys.revokeConfirm": "إبطال المفتاح «{name}»؟ ستتوقّف كل الأنظمة التي تستخدمه فورًا.",
  "settings.keys.empty": "لا توجد مفاتيح.",
  "settings.keys.emptyBody": "تسمح المفاتيح لحسّاسات الخزّانات والأنظمة الخارجية بإرسال القراءات.",
  "settings.keys.scope.ingest": "إرسال القراءات",
  "settings.keys.scope.read": "قراءة البيانات",
  "settings.keys.created": "أُنشئ المفتاح",
  "settings.keys.showOnce":
    "هذه هي المرة الوحيدة التي سيظهر فيها هذا المفتاح. انسخه الآن — لا نستطيع أن نعرضه عليك مرة أخرى.",
  "settings.keys.showOnceBody":
    "نحتفظ ببصمة مشفّرة للمفتاح فقط، لا بالمفتاح نفسه. إن فقدته، فالحلّ الوحيد هو إبطاله وإنشاء غيره.",
  "settings.keys.copy": "انسخ المفتاح",
  "settings.keys.ack": "حفظتُه",
  "settings.ownerOnly": "المالك فقط يستطيع إدارة مفاتيح الواجهة البرمجية.",

  // ---- settings: the copy this screen needed and did not have
  "settings.org.section.alerts": "متى ينبّهك تانكلي",
  "settings.org.thresholdSentence": "ننبّه فريقك عندما ينزل أي خزّان إلى {n}% من سعته أو أقلّ.",
  "settings.org.overdueSentence": "ونطارد أي موقع مضى عليه {n} يومًا دون تعبئة.",
  "settings.org.contactHint": "إلى هذا البريد تصل تنبيهات الوقود المنخفض والتعبئات المتأخّرة.",
  "settings.members.removeTitle": "إزالة عضو",
  "settings.members.leaveTitle": "مغادرة المؤسسة",
  "settings.members.leaveConfirm": "ستفقد وصولك إلى {org} فورًا.",
  "settings.members.lastOwnerRule":
    "يجب أن يبقى للمؤسسة مالك واحد على الأقل. لنقل الملكية، رقِّ عضوًا آخر إلى «مالك» أوّلًا — بعدها يمكنك تغيير دورك أو مغادرة المؤسسة.",
  "settings.keys.revokeTitle": "إبطال المفتاح",
  "settings.keys.expiry": "تاريخ الانتهاء",
  "settings.keys.expiryHint": "اتركه فارغًا ويبقى المفتاح صالحًا حتى تبطله بنفسك.",
  "settings.keys.noExpiry": "لا ينتهي",
  "settings.keys.scopesHint": "ما الذي يُسمح لهذا المفتاح بفعله.",
  "settings.keys.scopesRequired": "اختر صلاحية واحدة على الأقل.",
  "settings.keys.scope.ingestHint": "إرسال قراءات مستوى الخزّانات إلى تانكلي.",
  "settings.keys.scope.readHint": "قراءة المواقع والخزّانات والتعبئات.",
  // ---- field boards (ESP32 generator monitors)
  "devices.title": "الأجهزة الميدانية",
  "devices.subtitle": "لوحات مراقبة المولّدات في مواقعك، وما تقرؤه الآن.",
  "devices.online": "متصل",
  "devices.offline": "غير متصل",
  "devices.simulated": "بيانات تجريبية",
  "devices.simulatedHint": "هذه اللوحة ترسل قيمًا محاكاة، وليست قراءات حقيقية من المولّد.",
  "devices.live": "مباشر من وحدة التحكّم",
  "devices.liveHint": "تقرأ اللوحة هذه القيم من وحدة تحكّم المولّد نفسها.",
  "devices.controllerOffline": "اللوحة متصلة — وحدة تحكّم المولّد لا تستجيب",
  "devices.controllerOfflineHint":
    "اللوحة تصل إلى الخادم، لكن وحدة تحكّم المولّد لا تردّ على استعلاماتها، فلا توجد قراءات لعرضها. راجع وصلة RS485 والتغذية الكهربائية للوحدة.",
  "devices.notProvided": "لا توفّرها وحدة التحكّم",
  // `{when}` is a phrase from Intl.RelativeTimeFormat ("قبل ١٠ ثوانٍ").
  "devices.lastSeen": "آخر اتصال {when}",
  "devices.neverSeen": "لم تتصل بعد",
  "devices.lastSeenLabel": "آخر اتصال",
  "devices.firmware": "البرنامج الثابت",
  "devices.uptime": "مدة التشغيل",
  "devices.signal": "إشارة Wi-Fi",
  "devices.hardwareId": "معرّف العتاد",
  "devices.linkedGenerator": "المولّد المرتبط",
  "devices.notLinked": "غير مرتبطة بمولّد",
  "devices.noReading": "لم تصل أي قراءة بعد.",
  "devices.emptyTitle": "لا توجد لوحات بعد",
  "devices.emptyBody":
    "تظهر اللوحة هنا تلقائيًا عند أول قراءة ترسلها. برمجها بمفتاح API من هذه المؤسسة يحمل صلاحية «إرسال القراءات» (ingest:write).",
  "devices.emptyAction": "الإعدادات ← مفاتيح API",
  "devices.back": "كل الأجهزة",
  "devices.generatorSection": "المولّد الآن",
  "devices.tankSection": "الخزّان الآن",
  "devices.trendsSection": "آخر ساعة",
  "devices.noTrend": "لا قراءات لهذا المقياس في آخر ساعة.",
  "devices.engine": "المحرّك",
  "devices.engineState.running": "يعمل",
  "devices.engineState.stopped": "متوقّف",
  "devices.engineState.starting": "قيد الإقلاع",
  "devices.engineState.stopping": "قيد الإيقاف",
  "devices.engineState.error": "عُطل",
  "devices.controlMode": "وضع التشغيل",
  "devices.controlMode.stop": "إيقاف",
  "devices.controlMode.auto": "تلقائي",
  "devices.controlMode.manual": "يدوي",
  "devices.controlMode.test_on_load": "اختبار على الحمل",
  "devices.controlMode.test_off_load": "اختبار بلا حمل",
  "devices.controlMode.auto_manual_restore": "تلقائي (استعادة يدوية)",
  "devices.controlMode.user_config": "إعداد المستخدم",
  "devices.metric.voltage": "الجهد",
  "devices.metric.current": "التيار",
  "devices.metric.frequency": "التردد",
  "devices.metric.power": "القدرة",
  "devices.metric.fuel": "الوقود",
  "devices.metric.oilPressure": "ضغط الزيت",
  "devices.metric.coolant": "حرارة المبرّد",
  "devices.metric.battery": "بطارية التشغيل",
  "devices.metric.runHours": "ساعات التشغيل",
  "devices.metric.rpm": "سرعة المحرّك",
  "devices.metric.oilTemp": "حرارة الزيت",
  "devices.metric.chargeAlt": "دينمو الشحن",
  "devices.metric.load": "الحمل",
  "devices.metric.energy": "الطاقة المولّدة",
  "devices.metric.starts": "مرّات التشغيل",
  "devices.metric.tankLevel": "مستوى الخزّان",
  "devices.metric.tankHeight": "ارتفاع الوقود",
  "devices.metric.tankVolume": "حجم الوقود",
  "devices.metric.tankTemperature": "حرارة الوقود",
  "devices.unit.volt": "فولت",
  "devices.unit.amp": "أمبير",
  "devices.unit.hertz": "هرتز",
  "devices.unit.kw": "كيلوواط",
  "devices.unit.bar": "بار",
  "devices.unit.mm": "مم",
  "devices.unit.rpm": "دورة/د",
  "devices.unit.kwh": "كيلوواط·ساعة",
  "devices.unit.hourShort": "س",
  "devices.unit.minuteShort": "د",
  "devices.phases.title": "القياسات لكل طور",
  "devices.phases.phase": "الطور",
  "devices.phases.total": "الإجمالي",
  "devices.logs.title": "سجلّ اللوحة",
  "devices.logs.pause": "إيقاف مؤقّت",
  "devices.logs.resume": "استئناف",
  "devices.logs.following": "متابعة مباشرة",
  "devices.logs.paused": "متوقّف مؤقّتًا",
  "devices.logs.empty": "لم تصل أي أسطر من السجلّ بعد.",
  "devices.logs.count": "{count} سطر",
  "devices.logs.retention": "تُحفظ أسطر السجلّ 14 يومًا.",
  "devices.console.title": "وحدة التحكّم",
  "devices.console.hint": "يُنفَّذ الأمر عند المزامنة التالية للّوحة — خلال ثوانٍ إن كانت متصلة.",
  "devices.console.offline": "اللوحة غير متصلة الآن؛ سيبقى الأمر في الانتظار حتى تعود.",
  "devices.console.label": "الأمر",
  "devices.console.placeholder": "اكتب أمرًا، مثل status",
  "devices.console.send": "إرسال",
  "devices.console.quick": "أوامر سريعة",
  "devices.console.status.queued": "في الانتظار",
  "devices.console.status.sent": "أُرسل إلى اللوحة",
  "devices.console.status.done": "اكتمل",
  "devices.console.waiting": "بانتظار ردّ اللوحة…",
  "devices.console.noOutput": "(لا مخرجات)",
  "devices.console.recent": "الأوامر الأخيرة",
  "devices.console.none": "لم يُرسل أي أمر بعد.",
  "devices.console.by": "بواسطة {name}",
  "devices.console.confirmTitle": "تأكيد الأمر «{command}»",
  "devices.console.confirmReboot": "ستُعيد اللوحة تشغيل نفسها، وتتوقّف القراءات لحظات حتى تعود.",
  "devices.console.confirmUpdate":
    "ستنزّل اللوحة صورة البرنامج الثابت الحالية وتثبّتها ثم تُعيد التشغيل. لا تقطع الكهرباء عنها أثناء ذلك.",
  "devices.console.confirmSend": "نعم، أرسل الأمر",
  "devices.manage.title": "إدارة اللوحة",
  "devices.manage.name": "اسم اللوحة",
  "devices.manage.nameHint": "اسم يعرفه فريقك، مثل «لوحة مولّد الكرادة».",
  "devices.manage.saved": "تم الحفظ.",
  "devices.manage.generator": "المولّد الذي تراقبه",
  "devices.manage.generatorHint": "تُضاف ساعات التشغيل الحقيقية من هذه اللوحة إلى عدّاد المولّد المختار.",
  "devices.manage.noGenerator": "بلا مولّد",
  "devices.firmware.title": "البرنامج الثابت",
  "devices.firmware.none": "لم تُرفع أي صورة برنامج ثابت بعد.",
  "devices.firmware.version": "الإصدار",
  "devices.firmware.untagged": "بلا رقم إصدار",
  "devices.firmware.size": "الحجم",
  "devices.firmware.uploaded": "رُفعت",
  "devices.firmware.boardRuns": "هذه اللوحة تعمل بالإصدار {version}.",
  "devices.firmware.file": "ملف البرنامج الثابت (‎.bin)",
  "devices.firmware.upload": "رفع الصورة",
  "devices.firmware.uploading": "جارٍ الرفع…",
  "devices.firmware.uploadHint": "تصبح الصورة الحالية فورًا، لكن لن تثبّتها أي لوحة حتى ترسل لها الأمر update.",
  "devices.firmware.ownerOnly": "المالك وحده يستطيع رفع صورة برنامج ثابت جديدة.",
} as const;

export type TKey = keyof typeof ar;

/** English must define exactly the keys Arabic does — no more, no fewer. */
const en: Record<TKey, string> = {
  "app.name": "Tankly",
  "app.tagline": "The operating system for backup-power fuel",
  "nav.dashboard": "Dashboard",
  "nav.sites": "Sites",
  "nav.monitoring": "Monitoring",
  "nav.devices": "Devices",
  "nav.refills": "Refills",
  "nav.requests": "Requests",
  "nav.reports": "Reports",
  "nav.settings": "Settings",
  "nav.signOut": "Sign out",
  "nav.skipToContent": "Skip to content",
  "lang.toggle": "العربية",
  "lang.toggleShort": "ع",
  "lang.switchTo": "Switch to Arabic",

  "common.save": "Save",
  "common.saving": "Saving…",
  "common.cancel": "Cancel",
  "common.create": "Create",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.close": "Close",
  "common.back": "Back",
  "common.next": "Next",
  "common.search": "Search",
  "common.filter": "Filter",
  "common.clear": "Clear",
  "common.retry": "Try again",
  "common.loading": "Loading…",
  "common.optional": "Optional",
  "common.required": "Required",
  "common.none": "—",
  "common.all": "All",
  "common.liters": "L",
  "common.hours": "h",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.loadMore": "Load more",
  "common.of": "of",
  "common.actions": "Actions",
  "common.continue": "Continue",
  "common.skip": "Skip",
  "common.done": "Finish",

  "error.title": "Could not load this",
  "error.generic": "Something went wrong. Try again.",
  "error.network": "Cannot reach the server. Check your connection.",
  "error.notFound": "Not found",
  "error.notFoundBody": "The page you are looking for does not exist.",
  "error.forbidden": "You do not have permission to do that",
  "error.unauthorized": "Your session expired. Sign in again.",
  "empty.title": "Nothing here yet",

  "auth.tankSite": "Site",
  "auth.tankSiteName": "Karrada Branch",
  "auth.tankLow": "Runs dry in 4 days",
  "auth.tankLine":
    "Know how much fuel you have, where it went, and which site is about to run dry — before it does.",
  "auth.legalLine": "Tankly — the operating system for backup-power fuel",

  "auth.signIn": "Sign in",
  "auth.signInSubtitle": "Get back into your organization's workspace.",
  "auth.signUp": "Create an account",
  "auth.signUpSubtitle": "Start accounting for every liter.",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.name": "Full name",
  "auth.google": "Continue with Google",
  "auth.or": "or",
  "auth.forgot": "Forgot your password?",
  "auth.noAccount": "No account?",
  "auth.hasAccount": "Already have an account?",
  "auth.passwordHint": "At least 12 characters.",
  "auth.forgotTitle": "Reset your password",
  "auth.forgotSubtitle": "We will send a reset link if the address is registered.",
  "auth.forgotSend": "Send reset link",
  "auth.forgotSent": "If that address is registered, a reset link is on its way. Check your inbox.",
  "auth.resetTitle": "Choose a new password",
  "auth.resetSubtitle": "This link stops working once you use it.",
  "auth.resetSubmit": "Set password",
  "auth.resetDone": "Password updated. You can sign in now.",
  "auth.verifyTitle": "Verify your email",
  "auth.verifying": "Verifying…",
  "auth.verifyDone": "Your email is verified.",
  "auth.verifyFailed": "That verification link is invalid or has expired.",
  "auth.verifyResend": "Send a new verification email",
  "auth.verifySent": "A new verification email is on its way.",
  "auth.checkEmail": "Check your email",
  "auth.checkEmailBody": "We sent a verification link to {email}. Verify it, then sign in.",
  "auth.emailUnverified": "Your email is not verified yet.",

  "invite.title": "You have been invited",
  "invite.body": "{inviter} invited you to join {org} as {role}.",
  "invite.bodyNoInviter": "You have been invited to join {org} as {role}.",
  "invite.accept": "Accept invitation",
  "invite.accepting": "Accepting…",
  "invite.forEmail": "This invitation is for {email}. Sign in with that address to accept it.",
  "invite.expires": "This invitation expires on {date}.",
  "invite.invalid": "This invitation is not valid, or it has expired.",
  "invite.signInFirst": "Sign in to accept",

  "onboarding.step": "Step {n} of {total}",
  "onboarding.createOrg.title": "Create your organization's workspace",
  "onboarding.createOrg.subtitle":
    "Every tank, every generator, every liter — inside this workspace, and visible to nobody else.",
  "onboarding.createOrg.name": "Organization name",
  "onboarding.createOrg.nameHint": "In any language. We’ll handle the rest.",
  "onboarding.createOrg.namePlaceholder": "Your organization's name",
  "onboarding.createOrg.slug": "Workspace identifier",
  "onboarding.createOrg.slugHint": "This is your workspace URL.",
  "onboarding.createOrg.slugChecking": "Checking…",
  "onboarding.createOrg.slugAvailable": "Available",
  "onboarding.createOrg.slugTaken": "That identifier is taken.",
  "onboarding.createOrg.slugInvalid": "Lowercase letters, numbers and hyphens only.",
  "onboarding.createOrg.submit": "Create organization",
  "onboarding.invite.title": "Invite your team",
  "onboarding.invite.subtitle":
    "Fuel is a team sport. Add the people who record refills and the people who approve them.",
  "onboarding.invite.add": "Add another",
  "onboarding.invite.send": "Send invitations",
  "onboarding.invite.sent": "{n} invitation(s) sent.",
  "onboarding.invite.later": "I will do this later",
  "onboarding.site.title": "Add your first site",
  "onboarding.site.subtitle":
    "A site is the branch, the hospital, the tower — it is what tanks and generators belong to.",
  "onboarding.site.submit": "Create site",
  "onboarding.welcome": "Your workspace is ready.",
  "onboarding.rail.org": "Organization",
  "onboarding.rail.team": "Team",
  "onboarding.rail.site": "Site",
  "onboarding.site.created": "Site “{name}” created.",
  "onboarding.tank.title": "Now its first tank",
  "onboarding.tank.subtitle":
    "The tank is the thing we watch. Its capacity and its low-fuel threshold are what let Tankly warn you before the fuel runs out, rather than after.",
  "onboarding.tank.capacityHint": "How much the tank holds when it is full, in liters.",
  "onboarding.tank.initialHint": "How much is in it right now? Leave it empty if you do not know.",
  "onboarding.tank.submit": "Finish setup",
  "onboarding.tank.finishing": "Preparing your workspace…",

  "org.switcher.label": "Current organization",
  "org.switcher.switch": "Switch organization",
  "org.switcher.create": "Create an organization",
  "org.switcher.switching": "Switching…",
  "org.suspended": "This organization is suspended. Access is read-only.",
  "org.impersonating": "You are viewing this organization as Tankly support. This session is read-only.",

  "role.owner": "Owner",
  "role.admin": "Admin",
  "role.operator": "Operator",
  "role.viewer": "Viewer",
  "role.owner.hint": "Everything, including API keys.",
  "role.admin.hint": "Manage sites, refills and members.",
  "role.operator.hint": "Record refills and raise requests.",
  "role.viewer.hint": "Read-only.",

  "dash.title": "Dashboard",
  "dash.greeting": "Good morning, {name}",
  "dash.attention": "Needs your attention",
  "dash.attentionEmpty": "Nothing is about to go wrong.",
  "dash.attentionEmptyBody": "Every tank is above its threshold and no refill is overdue.",
  "dash.dry": "About to run dry",
  "dash.low": "Below threshold",
  "dash.overdue": "Refill overdue",
  "dash.pendingRequests": "Requests awaiting a decision",
  "dash.runsDry": "Dry {days}",
  "dash.runsDryUnknown": "Burn rate not known yet",
  "dash.lastRefill": "Last refill {when}",
  "dash.neverRefilled": "Never refilled",
  "dash.overdueBy": "Last refill {days}",
  "dash.healthy": "Healthy",
  "dash.remaining": "Liters left",
  "dash.runway": "Time left",
  "dash.recent": "Recent activity",
  "dash.viewAll": "View all",
  "dash.thisMonth": "This month",
  "dash.litersAdded": "Liters added",
  "dash.refillsRecorded": "Refills recorded",
  "dash.litersPerHour": "Liters/hour",
  "dash.sitesReporting": "Sites reporting",
  "dash.fleet": "Tank levels",
  "dash.viewSite": "View site",
  "dash.recordRefill": "Record refill",
  "dash.reviewRequest": "Review request",

  "sites.title": "Sites",
  "sites.new": "New site",
  "sites.empty": "No sites yet.",
  "sites.emptyBody":
    "A site is the branch, the hospital, the tower. Add your first one to start recording refills.",
  "sites.name": "Name",
  "sites.code": "Code",
  "sites.region": "Region",
  "sites.address": "Address",
  "sites.status": "Status",
  "sites.generators": "Generators",
  "sites.tanks": "Tanks",
  "sites.lastRefill": "Last refill",
  "sites.searchPlaceholder": "Search by name or code…",
  "sites.status.active": "Active",
  "sites.status.inactive": "Inactive",
  "sites.detail.tanks": "Tanks",
  "sites.detail.generators": "Generators",
  "sites.detail.history": "Refill history",
  "sites.detail.noTanks": "This site has no tanks.",
  "sites.detail.noGenerators": "This site has no generators.",
  "sites.detail.noRefills": "No refills recorded at this site.",
  "sites.addTank": "Add tank",
  "sites.addGenerator": "Add generator",
  "sites.deleteConfirm": "Delete {name}? Its generators and tanks go with it.",

  "sites.worstTank": "Lowest tank",
  "sites.noTanksYet": "No tanks",
  "sites.allRegions": "All regions",
  "sites.filterRegion": "Filter by region",
  "sites.edit": "Edit site",
  "sites.editTitle": "Edit site",
  "sites.noRegion": "No region",

  "sites.archive": "Archive site",
  "sites.archiveTitle": "Archive {name}?",
  "sites.archiveBody":
    "The site leaves your lists, stops raising alerts, and accepts no new refills. Its entire fuel history stays in the ledger and in reports, and you can put it back into service whenever you like. Nothing is deleted.",
  "sites.archived": "Archived",
  "sites.archivedNotice":
    "This site is archived: it accepts no new refills, and its history is kept exactly as it was.",
  "sites.restore": "Return to service",
  "sites.showArchived": "Show archived",

  "monitoring.title": "Monitoring",
  "monitoring.subtitle": "Live tank and generator readings, straight from the devices on site.",
  "monitoring.live": "Live",
  "monitoring.updated": "Updated {when}",
  "monitoring.neverReported": "Never reported",
  "monitoring.tanksSection": "Live tank levels",
  "monitoring.generatorsSection": "Live generator status",
  "monitoring.tanksEmpty": "No active tanks yet.",
  "monitoring.generatorsEmpty": "No active generators yet.",
  "monitoring.tankTrendTitle": "Fuel level trend",
  "monitoring.generatorTrendTitle": "Running-hours trend",
  "monitoring.last24h": "Last 24 hours",
  "monitoring.selectTank": "Select a tank",
  "monitoring.selectGenerator": "Select a generator",
  "monitoring.noTankSelected": "No tanks to chart yet.",
  "monitoring.noGeneratorSelected": "No generators to chart yet.",
  "monitoring.noReadingsTitle": "No live readings yet",
  "monitoring.noReadingsTankBody": "Connect a device to this tank to start the live feed here.",
  "monitoring.noReadingsGeneratorBody": "Connect a device to this generator to start the live feed here.",
  "monitoring.temperature": "Temperature",
  "monitoring.chartSummary": "{from} to {to}",

  "tank.name": "Tank name",
  "tank.capacity": "Capacity",
  "tank.level": "Level",
  "tank.fuelType": "Fuel",
  "tank.threshold": "Low threshold",
  "tank.thresholdHint":
    "Percentage of capacity at or below which this tank counts as low. Leave empty to use the organization setting.",
  "tank.initialLevel": "Opening level",
  "tank.levelUnknown": "Level unknown",
  "tank.fuel.diesel": "Diesel",
  "tank.fuel.petrol": "Petrol",
  "tank.fuel.kerosene": "Kerosene",
  "tank.status.active": "Active",
  "tank.status.decommissioned": "Decommissioned",
  "gen.name": "Generator name",
  "gen.kva": "Rating (kVA)",
  "gen.runningHours": "Running hours",
  "gen.manufacturer": "Manufacturer",
  "gen.model": "Model",
  "gen.serial": "Serial number",
  "gen.status.active": "Active",
  "gen.status.maintenance": "Maintenance",
  "gen.status.decommissioned": "Decommissioned",

  "tank.initialLevelHint":
    "A reading, not a refill. A tank recorded low will raise a low-fuel alert straight away — and it should.",
  "tank.capacityBelowLevel":
    "Below the last recorded level ({level} L). A tank cannot be smaller than the fuel already in it.",
  "tank.edit": "Edit tank",
  "tank.editTitle": "Edit {name}",
  "tank.statusLabel": "Status",

  "tank.decommission": "Decommission",
  "tank.decommissionTitle": "Decommission {name}?",
  "tank.decommissionBody":
    "The tank leaves your lists and accepts no new refills or readings. Every liter it has ever recorded stays in the ledger and in reports. Nothing is deleted, and you can put it back into service later.",
  "tank.recommission": "Return to service",
  "tank.outOfService": "Out of service ({n})",
  "tank.hideOutOfService": "Hide out of service",

  "gen.edit": "Edit generator",
  "gen.editTitle": "Edit {name}",
  "gen.statusLabel": "Status",
  "gen.runningHoursHint": "The meter never runs backwards. Last reading was {current} h.",
  "gen.decommission": "Decommission",
  "gen.decommissionTitle": "Decommission {name}?",
  "gen.decommissionBody":
    "The generator leaves your lists and cannot be picked on a new refill. Its running hours and every refill it appears in stay in the ledger and in reports. Nothing is deleted, and you can put it back into service later.",
  "gen.recommission": "Return to service",
  "gen.outOfService": "Out of service ({n})",
  "gen.hideOutOfService": "Hide out of service",

  "refills.title": "Refills",
  "refills.new": "Record refill",
  "refills.empty": "No refills recorded yet.",
  "refills.emptyBody": "Every liter that enters a tank is written here. This is the fuel ledger.",
  "refills.site": "Site",
  "refills.tank": "Tank",
  "refills.generator": "Generator",
  "refills.before": "Before",
  "refills.added": "Added",
  "refills.after": "After",
  "refills.workingHours": "Working hours",
  "refills.status": "Status",
  "refills.recordedAt": "Recorded at",
  "refills.recordedBy": "Recorded by",
  "refills.notes": "Notes",
  "refills.notesPlaceholder": "Supplier, truck number, any discrepancy…",
  "refills.status.pending": "Pending",
  "refills.status.completed": "Completed",
  "refills.status.cancelled": "Cancelled",
  "refills.arithmetic": "The arithmetic",
  "refills.checkOk": "The arithmetic checks out.",
  "refills.checkMismatch": "Does not add up: {before} + {added} = {expected}, not {after}.",
  "refills.checkOverCapacity": "Over the tank's capacity ({capacity} L).",
  "refills.checkHoursBackwards": "Hour meters do not run backwards. Last reading was {current}.",
  "refills.autoAfter": "computed",
  "refills.selectTank": "Select a tank",
  "refills.selectSite": "Select a site",
  "refills.selectGenerator": "No generator",
  "refills.tankLevelNow": "Last recorded level: {liters} L",
  "refills.saved": "Refill recorded.",
  "refills.deleteConfirm": "Delete this refill?",
  "refills.fromRequest": "Fulfilling request",
  "refills.dateFrom": "From",
  "refills.dateTo": "To",
  "refills.amend": "Amend",
  "refills.amendTitle": "Amend refill",
  "refills.amendHint":
    "Only a pending refill's figures can be changed. A completed one is an audited record.",
  "refills.amendStatus": "Status after amending",
  "refills.amended": "Refill amended.",
  "refills.deleteBody":
    "The refill is removed for good. A completed refill cannot be deleted — cancel it instead, so the fuel ledger stays auditable.",
  "refills.closedRecord": "A completed refill is an audited record and cannot be amended.",
  "refills.emptyFiltered": "No refills match this filter.",

  "requests.title": "Refill requests",
  "requests.new": "Request fuel",
  "requests.empty": "No requests.",
  "requests.emptyBody": "Operators raise a request when a site needs fuel; admins approve it.",
  "requests.requested": "Requested",
  "requests.approved": "Approved",
  "requests.neededBy": "Needed by",
  "requests.requestedBy": "Raised by",
  "requests.decidedBy": "Decided by",
  "requests.notes": "Justification",
  "requests.notesPlaceholder": "Tank at 18%, two outages expected this week.",
  "requests.decisionNote": "Decision note",
  "requests.approve": "Approve",
  "requests.decline": "Decline",
  "requests.cancel": "Withdraw",
  "requests.approveTitle": "Approve request",
  "requests.approveLiters": "Liters approved",
  "requests.approveHint": "You can approve less than was asked for.",
  "requests.declineTitle": "Decline request",
  "requests.declineReason": "Reason",
  "requests.declineReasonHint": "The operator sees this. Required — never leave them guessing.",
  "requests.fulfil": "Record the refill",
  "requests.status.pending": "Awaiting decision",
  "requests.status.approved": "Approved",
  "requests.status.declined": "Declined",
  "requests.status.fulfilled": "Fulfilled",
  "requests.status.cancelled": "Withdrawn",

  "reports.title": "Reports",
  "reports.from": "From",
  "reports.to": "To",
  "reports.run": "Run report",
  "reports.export": "Export",
  "reports.exportTitle": "Export a report",
  "reports.type": "Report",
  "reports.format": "Format",
  "reports.type.refills": "Refill ledger",
  "reports.type.consumption": "Consumption and efficiency",
  "reports.type.sites": "Site inventory",
  "reports.exports": "Exports",
  "reports.exportsEmpty": "No exports yet.",
  "reports.download": "Download",
  "reports.export.pending": "Rendering",
  "reports.export.ready": "Ready",
  "reports.export.failed": "Failed",
  "reports.export.expired": "Expired",
  "reports.requested": "Export requested. It will appear here when it is ready.",
  "reports.noData": "No data in this window.",
  "reports.perSite": "By site",
  "reports.range.30": "Last 30 days",
  "reports.range.90": "Last 90 days",
  "reports.range.month": "This month",
  "reports.range.year": "This year",

  "settings.title": "Settings",
  "settings.profile": "Organization",
  "settings.members": "Members",
  "settings.invitations": "Invitations",
  "settings.apiKeys": "API keys",
  "settings.org.name": "Organization name",
  "settings.org.slug": "Identifier",
  "settings.org.slugImmutable": "The identifier cannot be changed after creation.",
  "settings.org.timezone": "Timezone",
  "settings.org.threshold": "Low-fuel threshold",
  "settings.org.thresholdHint": "Tanks count as low at or below this percentage of capacity.",
  "settings.org.overdue": "Refill overdue after",
  "settings.org.overdueHint": "A site is flagged overdue after this many days without a refill.",
  "settings.org.contact": "Alerts email",
  "settings.saved": "Changes saved.",
  "settings.members.invite": "Invite a member",
  "settings.members.role": "Role",
  "settings.members.joined": "Joined",
  "settings.members.lastActive": "Last active",
  "settings.members.remove": "Remove",
  "settings.members.removeConfirm": "Remove {name} from the organization?",
  "settings.members.leave": "Leave organization",
  "settings.members.you": "you",
  "settings.members.lastOwner": "The last owner cannot be removed.",
  "settings.invite.email": "Email",
  "settings.invite.role": "Role",
  "settings.invite.send": "Send invitation",
  "settings.invite.pending": "Pending invitations",
  "settings.invite.none": "No pending invitations.",
  "settings.invite.revoke": "Revoke",
  "settings.invite.expires": "Expires",
  "settings.invite.invitedBy": "Invited by",
  "settings.invite.sent": "Invitation sent to {email}.",
  "settings.keys.new": "New key",
  "settings.keys.name": "Name",
  "settings.keys.prefix": "Prefix",
  "settings.keys.scopes": "Scopes",
  "settings.keys.lastUsed": "Last used",
  "settings.keys.expires": "Expires",
  "settings.keys.never": "Never used",
  "settings.keys.revoke": "Revoke",
  "settings.keys.revokeConfirm":
    "Revoke “{name}”? Every system using it stops working immediately.",
  "settings.keys.empty": "No API keys.",
  "settings.keys.emptyBody": "Keys let tank sensors and external systems push readings in.",
  "settings.keys.scope.ingest": "Push readings",
  "settings.keys.scope.read": "Read data",
  "settings.keys.created": "Key created",
  "settings.keys.showOnce":
    "This is the only time this key will be shown. Copy it now — we cannot show it to you again.",
  "settings.keys.showOnceBody":
    "We store only a hash of it, never the key itself. If you lose it, the only fix is to revoke it and mint another.",
  "settings.keys.copy": "Copy key",
  "settings.keys.ack": "I have saved it",
  "settings.ownerOnly": "Only the owner can manage API keys.",

  // ---- settings: the copy this screen needed and did not have
  "settings.org.section.alerts": "When Tankly warns you",
  "settings.org.thresholdSentence": "We alert your team when any tank drops to {n}% of its capacity or below.",
  "settings.org.overdueSentence": "And we chase any site that has gone {n} days without a refill.",
  "settings.org.contactHint": "Low-fuel and overdue-refill alerts are emailed here.",
  "settings.members.removeTitle": "Remove member",
  "settings.members.leaveTitle": "Leave organization",
  "settings.members.leaveConfirm": "You lose access to {org} immediately.",
  "settings.members.lastOwnerRule":
    "An organization must always have at least one owner. To hand ownership over, promote someone else to Owner first — then you can change your own role or leave.",
  "settings.keys.revokeTitle": "Revoke key",
  "settings.keys.expiry": "Expiry date",
  "settings.keys.expiryHint": "Leave it empty and the key stays valid until you revoke it yourself.",
  "settings.keys.noExpiry": "No expiry",
  "settings.keys.scopesHint": "What this key is allowed to do.",
  "settings.keys.scopesRequired": "Choose at least one scope.",
  "settings.keys.scope.ingestHint": "Push tank level readings into Tankly.",
  "settings.keys.scope.readHint": "Read sites, tanks and refills.",
  // ---- field boards (ESP32 generator monitors)
  "devices.title": "Field devices",
  "devices.subtitle": "The generator-monitor boards on your sites, and what they are reading right now.",
  "devices.online": "Online",
  "devices.offline": "Offline",
  "devices.simulated": "Simulated data",
  "devices.simulatedHint": "This board is sending simulated values, not real readings from the generator.",
  "devices.live": "Live from controller",
  "devices.liveHint": "These values are read from the generator's own controller.",
  "devices.controllerOffline": "Board online — generator controller not answering",
  "devices.controllerOfflineHint":
    "The board is reaching the server, but the generator's controller is not answering it, so there are no readings to show. Check the RS485 link and the controller's power.",
  "devices.notProvided": "Not provided by the controller",
  "devices.lastSeen": "Last seen {when}",
  "devices.neverSeen": "Never seen",
  "devices.lastSeenLabel": "Last seen",
  "devices.firmware": "Firmware",
  "devices.uptime": "Uptime",
  "devices.signal": "Wi-Fi signal",
  "devices.hardwareId": "Hardware ID",
  "devices.linkedGenerator": "Linked generator",
  "devices.notLinked": "Not linked to a generator",
  "devices.noReading": "No reading has arrived yet.",
  "devices.emptyTitle": "No boards yet",
  "devices.emptyBody":
    "A board appears here on its own the first time it reports. Flash it with an API key from this organization that has the ingest:write scope.",
  "devices.emptyAction": "Settings → API keys",
  "devices.back": "All devices",
  "devices.generatorSection": "Generator now",
  "devices.tankSection": "Tank now",
  "devices.trendsSection": "Last hour",
  "devices.noTrend": "No readings for this measure in the last hour.",
  "devices.engine": "Engine",
  "devices.engineState.running": "Running",
  "devices.engineState.stopped": "Stopped",
  "devices.engineState.starting": "Starting",
  "devices.engineState.stopping": "Stopping",
  "devices.engineState.error": "Error",
  "devices.controlMode": "Control mode",
  "devices.controlMode.stop": "Stop",
  "devices.controlMode.auto": "Auto",
  "devices.controlMode.manual": "Manual",
  "devices.controlMode.test_on_load": "Test on load",
  "devices.controlMode.test_off_load": "Test off load",
  "devices.controlMode.auto_manual_restore": "Auto (manual restore)",
  "devices.controlMode.user_config": "User config",
  "devices.metric.voltage": "Voltage",
  "devices.metric.current": "Current",
  "devices.metric.frequency": "Frequency",
  "devices.metric.power": "Power",
  "devices.metric.fuel": "Fuel",
  "devices.metric.oilPressure": "Oil pressure",
  "devices.metric.coolant": "Coolant temp",
  "devices.metric.battery": "Starter battery",
  "devices.metric.runHours": "Run hours",
  "devices.metric.rpm": "Engine speed",
  "devices.metric.oilTemp": "Oil temperature",
  "devices.metric.chargeAlt": "Charge alternator",
  "devices.metric.load": "Load",
  "devices.metric.energy": "Energy",
  "devices.metric.starts": "Starts",
  "devices.metric.tankLevel": "Tank level",
  "devices.metric.tankHeight": "Fuel height",
  "devices.metric.tankVolume": "Fuel volume",
  "devices.metric.tankTemperature": "Fuel temperature",
  "devices.unit.volt": "V",
  "devices.unit.amp": "A",
  "devices.unit.hertz": "Hz",
  "devices.unit.kw": "kW",
  "devices.unit.bar": "bar",
  "devices.unit.mm": "mm",
  "devices.unit.rpm": "RPM",
  "devices.unit.kwh": "kWh",
  "devices.unit.hourShort": "h",
  "devices.unit.minuteShort": "m",
  "devices.phases.title": "Per phase",
  "devices.phases.phase": "Phase",
  "devices.phases.total": "Total",
  "devices.logs.title": "Board log",
  "devices.logs.pause": "Pause",
  "devices.logs.resume": "Resume",
  "devices.logs.following": "Following",
  "devices.logs.paused": "Paused",
  "devices.logs.empty": "No log lines have arrived yet.",
  "devices.logs.count": "{count} lines",
  "devices.logs.retention": "Log lines are kept for 14 days.",
  "devices.console.title": "Console",
  "devices.console.hint": "A command runs at the board's next sync — within seconds while it is online.",
  "devices.console.offline": "The board is offline; the command will wait until it is back.",
  "devices.console.label": "Command",
  "devices.console.placeholder": "Type a command, e.g. status",
  "devices.console.send": "Send",
  "devices.console.quick": "Quick commands",
  "devices.console.status.queued": "Queued",
  "devices.console.status.sent": "Sent to board",
  "devices.console.status.done": "Done",
  "devices.console.waiting": "Waiting for the board…",
  "devices.console.noOutput": "(no output)",
  "devices.console.recent": "Recent commands",
  "devices.console.none": "No commands sent yet.",
  "devices.console.by": "by {name}",
  "devices.console.confirmTitle": "Send “{command}”?",
  "devices.console.confirmReboot": "The board will restart itself; readings pause for a moment until it is back.",
  "devices.console.confirmUpdate":
    "The board will download the current firmware image, install it and restart. Do not cut its power while it does.",
  "devices.console.confirmSend": "Yes, send it",
  "devices.manage.title": "Manage board",
  "devices.manage.name": "Board name",
  "devices.manage.nameHint": "A name your team recognises, like “Karrada generator board”.",
  "devices.manage.saved": "Saved.",
  "devices.manage.generator": "Generator it monitors",
  "devices.manage.generatorHint": "Real run hours from this board flow into the chosen generator's hour meter.",
  "devices.manage.noGenerator": "No generator",
  "devices.firmware.title": "Firmware",
  "devices.firmware.none": "No firmware image has been uploaded yet.",
  "devices.firmware.version": "Version",
  "devices.firmware.untagged": "Untagged",
  "devices.firmware.size": "Size",
  "devices.firmware.uploaded": "Uploaded",
  "devices.firmware.boardRuns": "This board runs {version}.",
  "devices.firmware.file": "Firmware file (.bin)",
  "devices.firmware.upload": "Upload image",
  "devices.firmware.uploading": "Uploading…",
  "devices.firmware.uploadHint": "It becomes the current image at once, but no board installs it until you send it the update command.",
  "devices.firmware.ownerOnly": "Only the owner can upload a new firmware image.",
};

export const dict: Record<Lang, Record<TKey, string>> = { ar, en };

/** `t("invite.body", { org: "Acme", role: "admin" })` — {placeholders} are substituted. */
export function translate(
  lang: Lang,
  key: TKey,
  vars?: Record<string, string | number>,
): string {
  const template = dict[lang][key];
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}
